import { useState } from 'react';
import { Box, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { useRecommendation } from '../../bff-api/bloomreach-recommendation/useRecommendation';
import { EventAction, EventLocation, useBloomreach } from '../../hooks/useBloomreach';
import { Header } from '../../ui-components';
import { Carousel } from '../../ui-components/carousel/Carousel';
import { ProductCard } from '../../ui-components/product-card/ProductCard';
import { classNames } from '../../utils/tailwind/classNames';
import { RecommendationLocation } from './RecommendationLocation';
import styles from './Recommendations.module.scss';
import { RecommendationsSkeleton } from './RecommendationsSkeleton';
import type { Product } from '../../bff-api/products/model';

export interface RecommendationProps {
    title: string;
    widgetType: string;
    widgetId: string;
    productId?: string;
    categoryId?: string;
    brandCategoryId?: string;
    collectionId?: string;
    maxResults?: number;
    location: RecommendationLocation;
    userId?: string;
}

const DRAWER_MAX_RESULTS = 5;

export const Recommendations = ({
    title,
    widgetType,
    widgetId,
    productId,
    categoryId,
    brandCategoryId,
    collectionId,
    maxResults,
    location,
    userId,
}: RecommendationProps): JSX.Element | null => {
    const { data, error, isError, isLoading } = useRecommendation({
        widgetType,
        widgetId,
        productId,
        categoryId,
        brandCategoryId,
        collectionId,
        maxResults,
        userId,
    });

    const { widgetRendered } = useBloomreach();

    const isBase = useBreakpointValue({ base: true, sm: false });

    const [addedProducts, setAddedProducts] = useState<Record<string, boolean>>({});

    if (isError && error) {
        throw error;
    }

    const isDrawer = location === RecommendationLocation.DRAWER;

    if (isLoading) {
        return <RecommendationsSkeleton isDrawer={isDrawer} />;
    }

    if (!data) {
        return null;
    }

    const { products } = data;
    const filteredProducts =
        (isDrawer ? products.slice(0, DRAWER_MAX_RESULTS) : products).filter(
            (p) => !addedProducts[p.id],
        ) ?? [];

    if (!filteredProducts.length) {
        return null;
    }

    const getOnAddProductToBag = (): ((product: Product) => void) | undefined =>
        location !== RecommendationLocation.SHOPPING_BAG
            ? (product: Product): void => {
                  setAddedProducts((current) => ({ ...current, [product.id]: true }));
              }
            : undefined;

    const onViewCarousel = (): void => {
        widgetRendered({
            location: EventLocation.widget,
            action: EventAction['widget-view'],
            data: {
                widgetResponseId: uuidv4(),
                widgetId,
                widgetType,
                metadataType: data.metadata.type,
            },
        });
    };

    if (isDrawer) {
        return (
            <div className="bg-white p-0">
                <Header as="h2" style="bold" variant="header4" color="slt-pepper.1000" padding="0">
                    <FormattedMessage
                        id="recommendations.we.think.you.will.also.love"
                        defaultMessage="We think you will also love"
                    />
                </Header>
                <div className="-mx-4">
                    <Carousel
                        id={widgetId}
                        products={filteredProducts.slice(0, DRAWER_MAX_RESULTS)}
                        swiperSliderClassName="swiper-slide-drawer"
                        wrapperClassName={styles.carouselSwiper}
                        onViewCarousel={onViewCarousel}
                        slidesOffset={16}
                        renderProduct={(product: Product) => (
                            <ProductCard
                                product={product}
                                location={location}
                                widgetId={widgetId}
                                widgetType={widgetType}
                                metadataType={data.metadata.type}
                                onAddProductToBag={getOnAddProductToBag()}
                            />
                        )}
                    />
                </div>
            </div>
        );
    }

    const classWrapper =
        location === RecommendationLocation.SHOPPING_BAG
            ? styles.recommendationsShoppingBag
            : undefined;
    const boxPadding = location === RecommendationLocation.SHOPPING_BAG ? '16px 0' : '0';
    const headerPadding =
        location === RecommendationLocation.SHOPPING_BAG && isBase ? '0 16px' : '0';
    const carouselSlidesOffset =
        location === RecommendationLocation.SHOPPING_BAG && isBase ? 16 : 0;

    return (
        <Box
            className={classNames('bg-white', classWrapper)}
            margin={{ xl: 'auto' }}
            padding={boxPadding}
        >
            <Header
                as="h2"
                style="bold"
                variant="header4"
                color="slt-pepper.1000"
                padding={headerPadding}
            >
                {title}
            </Header>

            <Carousel
                id={widgetId}
                products={filteredProducts}
                wrapperClassName={styles.carouselSwiper}
                onViewCarousel={onViewCarousel}
                slidesOffset={carouselSlidesOffset}
                renderProduct={(product: Product) => (
                    <ProductCard
                        product={product}
                        location={location}
                        widgetId={widgetId}
                        widgetType={widgetType}
                        metadataType={data.metadata.type}
                        onAddProductToBag={getOnAddProductToBag()}
                    />
                )}
            />
        </Box>
    );
};

Recommendations.defaultProps = {
    productId: undefined,
    categoryId: undefined,
    brandCategoryId: undefined,
    collectionId: undefined,
    maxResults: undefined,
    userId: undefined,
};
